import { FC, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import { DiningReservationTemplate } from 'voyager';
import type { DiningReservationRestaurant } from 'voyager';

import { Routes } from '@common/constants';
import { useTranslations } from '@lang';
import { yourJourneySelectors } from '@store/yourJourney';
import type { ParsedJourneyParams } from '@store/yourJourney/selectors';
import rewriteHttp from '@utils/rewriteHttp';

import type { DiningReservationV2Props } from './DiningReservationV2.types';
import { useDiningPreferences } from './useDiningPreferences';
import { useReservationBasketId } from './useReservationBasketId';
import { formatIsoDate, useDiningRestaurants, useRestaurantWeekAvailability } from './queries';

const DEFAULT_CURRENCY = 'GBP';
const MAX_GUESTS = 12;

function isoToday() {
  return formatIsoDate(new Date());
}

export const DiningReservationV2: FC<DiningReservationV2Props> = ({
  restaurantId,
  property,
  basketDetails,
  language,
  createBasket,
  updateBasket,
  fetchBasketDetails,
}) => {
  const { t } = useTranslations();
  const router = useRouter();
  const journey = useSelector(yourJourneySelectors.getParsedJourneyParams) as ParsedJourneyParams;
  const rawFromDate = useSelector(yourJourneySelectors.getActualFromDateValue);
  const searchedDate =
    rawFromDate && journey.fromDate?.isValid() ? journey.fromDate.format('YYYY-MM-DD') : undefined;
  const { basketId, basketNotReady } = useReservationBasketId({
    basketDetails,
    propertyId: property.id,
    language,
    fetchBasketDetails,
  });

  const { data: restaurantsData } = useDiningRestaurants();
  const restaurants = Array.isArray(restaurantsData) ? restaurantsData : [];

  const currency = property.defaultCurrency || DEFAULT_CURRENCY;
  const children = Number(journey.tableChildren ?? 0);

  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | undefined>(
    restaurantId
  );
  const [guestCount, setGuestCount] = useState(
    Math.min(Math.max(Number(journey.tableAdults) || 2, 1), MAX_GUESTS)
  );
  const [activeDate, setActiveDate] = useState(() => searchedDate ?? isoToday());
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
  const [selectedTimeslot, setSelectedTimeslot] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const selectedRestaurant =
    restaurants.find((restaurant) => restaurant.providerId === selectedRestaurantId) ?? null;

  const timeslots = useRestaurantWeekAvailability(
    selectedRestaurant,
    activeDate,
    guestCount,
    children
  );

  const dayAvailability = useMemo(() => {
    const byDate = new Map<string, boolean>();
    timeslots.forEach((slot) =>
      byDate.set(slot.date, (byDate.get(slot.date) ?? false) || slot.hasStandardAvailability)
    );
    return Array.from(byDate, ([date, available]) => ({ date, available }));
  }, [timeslots]);

  const dayTimeslots = useMemo(() => {
    const seenStartTimes = new Set<string>();
    return timeslots
      .filter((slot) => slot.date === selectedDate && slot.hasStandardAvailability)
      .filter((slot) => {
        if (seenStartTimes.has(slot.startTime)) return false;
        seenStartTimes.add(slot.startTime);
        return true;
      })
      .map((slot) => ({
        value: slot.startTime,
        label: slot.startTime,
      }));
  }, [timeslots, selectedDate]);

  const selectedSlot = timeslots.find(
    (slot) =>
      slot.date === selectedDate &&
      slot.startTime === selectedTimeslot &&
      slot.hasStandardAvailability
  );
  const depositAmount = Number(selectedSlot?.standardAvailabilityFeeAmount ?? 0);
  const depositMessage =
    depositAmount > 0
      ? t('dining.depositMessage', {
          amount: new Intl.NumberFormat(language, {
            style: 'currency',
            currency,
          }).format(depositAmount),
        })
      : undefined;

  const restaurantOptions: DiningReservationRestaurant[] = restaurants.map((restaurant) => ({
    id: restaurant.providerId,
    name: restaurant.name,
    subtitle: restaurant.subtitle ?? undefined,
    imageUrl: restaurant.imageUrls[0] ? rewriteHttp(restaurant.imageUrls[0]) : undefined,
  }));

  const menus =
    selectedRestaurant?.menus.flatMap((menu) =>
      menu.url ? [{ id: menu.id, title: menu.title, url: rewriteHttp(menu.url) }] : []
    ) ?? [];

  const heroSource = selectedRestaurant?.imageUrls[0] ?? restaurants[0]?.imageUrls[0];
  const heroImage = heroSource ? rewriteHttp(heroSource) : undefined;

  const selectRestaurant = (id: string) => {
    setSelectedRestaurantId(id);
    setSelectedDate(undefined);
    setSelectedTimeslot(undefined);
  };

  const preferences = useDiningPreferences();

  const handleReserve = async () => {
    if (!selectedRestaurant || !selectedSlot || submittingRef.current || basketNotReady) return;

    const tableItem = {
      type: 'tables',
      adults: guestCount,
      children,
      note: preferences.values.note,
      dietaryRequirements: preferences.values.dietaryRequirements,
      celebrations: preferences.values.celebrations,
      bookable: {
        ids: {
          timeslot: selectedSlot.timeSlot,
          restaurant: selectedRestaurant.restaurantName,
          restaurantName: selectedRestaurant.configName,
          restaurantConfig: selectedRestaurant.restaurantId,
          timeslotReservationId: selectedSlot.timeslotReservationId ?? undefined,
        },
      },
    };

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      if (basketId) {
        const existingItems = Array.isArray(basketDetails?.items) ? basketDetails.items : [];
        await updateBasket(property.id, basketId, language, {
          items: [tableItem, ...existingItems],
        });
      } else {
        await createBasket(property.id, language, { items: [tableItem] });
      }

      router.push({ pathname: Routes.BASKET });
    } catch {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <DiningReservationTemplate
      preferenceCategories={preferences.preferenceCategories}
      onPreferenceOptionToggle={preferences.onPreferenceOptionToggle}
      onPreferenceOtherChange={preferences.onPreferenceOtherChange}
      onPreferenceFreeTextChange={preferences.onPreferenceFreeTextChange}
      imageUrl={heroImage}
      imageAlt={selectedRestaurant?.name ?? t('app.dining')}
      onBack={() => router.push({ pathname: Routes.TABLES_HOME })}
      restaurants={restaurantOptions}
      selectedRestaurantId={selectedRestaurantId}
      onSelectRestaurant={selectRestaurant}
      guestCount={guestCount}
      minGuests={1}
      maxGuests={MAX_GUESTS}
      onGuestCountChange={(count) => {
        setGuestCount(count);
        setSelectedTimeslot(undefined);
      }}
      activeDate={activeDate}
      selectedDate={selectedDate}
      availability={dayAvailability}
      onDateSelect={(date) => {
        setSelectedDate(date);
        setSelectedTimeslot(undefined);
        setActiveDate(date);
      }}
      onWeekNavigate={(date) => {
        setActiveDate(date);
        setSelectedDate(undefined);
        setSelectedTimeslot(undefined);
      }}
      timeslots={dayTimeslots}
      selectedTimeslot={selectedTimeslot}
      onTimeslotSelect={setSelectedTimeslot}
      progressStep={selectedTimeslot ? 2 : 1}
      menus={menus}
      depositMessage={depositMessage}
      onReserve={handleReserve}
      reserveDisabled={isSubmitting || basketNotReady}
      labels={{
        heading: t('dining.reservationHeading'),
        subheading: t('dining.reservationSubheading'),
        restaurantTitle: t('dining.restaurantTitle'),
        restaurantSubtitle: t('dining.restaurantSubtitle'),
        reservationTitle: t('dining.reservationTitle'),
        reservationSubtitle: t('dining.reservationSubtitle'),
        guestLabel: t('dining.guests'),
        timeLabel: t('dining.time'),
        noTimeslotsLabel: t('dining.noTimeslots'),
        menuTitle: t('dining.menuTitle'),
        reserveLabel: t('dining.reserve'),
        back: t('dining.back'),
        noImage: t('basket.noImageLabel'),
        preferences: {
          title: t('dining.preferencesTitle'),
          otherButtonLabel: t('dining.preferencesOther'),
        },
      }}
    />
  );
};
